import React from 'react'

import { FormattedMessage } from 'react-intl'

import Box from 'app/components/base/Box'
import Skeleton from 'app/components/base/Skeleton'
import Stack from 'app/components/base/Stack'
import Text from 'app/components/base/Text'
import Compatibility from 'app/components/common/Compatibility'
import ShowMoreText from 'app/components/common/ShowMoreText'
import { IS_NATIVE_APP } from 'app/utils/constants/env.constants'

import { useDescription } from '../hooks/useDescription'

interface DescriptionProps {
  description: string
  subCategory: SubCategory
  shipping: ShippingModel
  year: string
  dimensions: string
  weight?: string
  package_name?: string
  loading?: boolean
  categoryOptions?: CategoryOption[]
  compatibilities?: ListingModel['compatibilities']
  partNumber?: string
}

const Description: RFC<DescriptionProps> = ({
  description,
  subCategory,
  year,
  dimensions,
  weight,
  package_name,
  loading,
  categoryOptions,
  compatibilities,
  partNumber
}) => {
  const { categoryOptionsNames, isFlatRate, showAttributes } = useDescription({
    package_name,
    categoryOptions
  })
  const hasDimensions = !!dimensions || !!weight
  const showPackageDimensions = !isFlatRate && hasDimensions

  return (
    <Stack spacing={2} sx={{ pb: 3 }}>
      <Skeleton loading={loading} width="100%">
        <Text variant="body2">
          {subCategory?.parent?.name}, {subCategory?.name}
          {showAttributes && (
            <>
              {' | '}
              <Text component="span" sx={{ color: 'text.third' }}>
                {categoryOptionsNames}
              </Text>
            </>
          )}
        </Text>
      </Skeleton>

      <Stack direction="column" spacing={1}>
        <Text variant="body2" sx={{ fontWeight: 500 }}>
          <FormattedMessage defaultMessage="Compatibility" />
        </Text>

        <Skeleton loading={loading} width="50%">
          <Compatibility compatibilities={compatibilities} />
        </Skeleton>
      </Stack>

      {!!partNumber && (
        <Stack direction="column" spacing={1}>
          <Text variant="body2" sx={{ fontWeight: 500 }}>
            <FormattedMessage defaultMessage="Part Number" />
          </Text>

          <Text
            variant="subtitle1"
            sx={{ ...(!IS_NATIVE_APP && { wordBreak: 'break-word' }) }}
          >
            {partNumber}
          </Text>
        </Stack>
      )}

      {!!year && (
        <>
          <Text variant="body2" sx={{ fontWeight: 500 }}>
            <FormattedMessage defaultMessage="Year" />
          </Text>

          <Text variant="body2" color="text.third">
            {year}
          </Text>
        </>
      )}

      {showPackageDimensions && (
        <>
          <Stack spacing={1}>
            <Text variant="body2" sx={{ fontWeight: 500 }}>
              <FormattedMessage defaultMessage="Package Dimensions" />
            </Text>

            <Box>
              <Skeleton loading={loading} width="100%">
                <Text variant="body2" color="text.third">
                  {dimensions}
                </Text>

                <Text variant="body2" color="text.third">
                  {weight}
                </Text>
              </Skeleton>
            </Box>
          </Stack>
        </>
      )}

      <Stack spacing={1}>
        <Text variant="body2" sx={{ fontWeight: 500 }}>
          <FormattedMessage defaultMessage="Description" />
        </Text>

        <Skeleton loading={loading} width="100%">
          <ShowMoreText
            text={description}
            textProps={{
              variant: 'body2',
              sx: {
                ...(!IS_NATIVE_APP && {
                  wordBreak: 'break-word',
                  whiteSpace: 'break-spaces'
                })
              }
            }}
          />
        </Skeleton>
      </Stack>
    </Stack>
  )
}

export default Description
